// The public client's private-site state (registration step 33;
// docs/pages/frontend-shell.md quirk 48, security-model.md › Private site).
//
// On a members-only league the server's gate (membersOnlyHook) answers every
// public read but a few open ones with 401 members_only unless the Bearer is
// a registered member of the league or its staff. This module is the one
// place the public client decides what Bearer to send and records what the
// gate said about it:
//
//   I1  On a league whose /tenant `visibility` is not 'members_only' (a
//       missing field counts as public) nothing changes: the slot is off,
//       publicCredential() returns null BEFORE touching storage, so no
//       Authorization header is sent, no token key is read and no probe runs.
//   I2  On a private league the public client sends the member token
//       (mcl_member_token, read only through memberApi.getMemberToken), else
//       the staff token (mcl_admin_token, read only through
//       sessionEnd.readStaffToken). Inside the admin Design preview frame the
//       order flips (staff first), so an admin previewing a private league is
//       admitted as staff even if this browser also holds a member token that
//       is not registered (T7). The staff clients never read the member token,
//       memberApi never reads a staff key, and nothing here ever clears or
//       ends the staff session: a `session_ended` for a staff credential only
//       records the verdict.
//
//   I3  A Growth preview (Ops › Growth: `preview` on /tenant) is private to
//       staff only (backend services/preview-site.ts): the public client sends
//       the staff token alone, never a member token, and the gate's 401
//       `private_site` is read like `members_only` (reason `ended` once the
//       preview is over).
//
// The verdict store keeps what the gate answered, per credential
// (`credentialKey`), so the site's SiteAccessProvider can derive the wall
// without an effect deciding it. No React here.

import { endMemberSessionFor, getMemberToken } from './memberApi'
import { readStaffToken } from './sessionEnd'

// ── The slot ───────────────────────────────────────────────────────────────

export type PrivateSiteKind = 'members' | 'preview'

let privateSite = false
let privateKind: PrivateSiteKind = 'members'
let preferStaff = false

/**
 * Set during LeagueProvider's render from the tenant: 'members' for a
 * members-only league (`visibility`), 'preview' for a Growth preview, false
 * (or true, an older caller: members) for a public one.
 */
export function setPrivateSite(on: boolean | PrivateSiteKind): void {
  privateSite = on !== false
  privateKind = on === 'preview' ? 'preview' : 'members'
}

export function isPrivateSite(): boolean {
  return privateSite
}

/** A private Growth preview: staff only. */
export function isPrivatePreviewSite(): boolean {
  return privateSite && privateKind === 'preview'
}

/** Set once by PreviewBridge when its frame is active: staff first. */
export function setPreferStaffCredential(on: boolean): void {
  preferStaff = on
}

// ── The credential ─────────────────────────────────────────────────────────

export type PublicCredential = { kind: 'member' | 'staff'; token: string }

/**
 * The Bearer the public client sends: null on a public league (no storage
 * read at all), else the member token, else the staff token (staff first in
 * the preview frame).
 */
export function publicCredential(): PublicCredential | null {
  if (!privateSite) return null
  const member = (): PublicCredential | null => {
    const token = getMemberToken()
    return token ? { kind: 'member', token } : null
  }
  const staff = (): PublicCredential | null => {
    const token = readStaffToken()
    return token ? { kind: 'staff', token } : null
  }
  if (privateKind === 'preview') return staff()
  return preferStaff ? staff() ?? member() : member() ?? staff()
}

/** '' for no credential, else `${kind}:${token}` (the verdict store's key). */
export function credentialKey(c: PublicCredential | null): string {
  return c ? `${c.kind}:${c.token}` : ''
}

/** {} on a public league or without a credential, else the Bearer header. */
export function publicAuthHeaders(sent: PublicCredential | null = publicCredential()): Record<string, string> {
  return sent ? { Authorization: `Bearer ${sent.token}` } : {}
}

// ── What the gate said ─────────────────────────────────────────────────────

export type SiteRefusal = 'signed_out' | 'session_ended' | 'not_member' | 'not_registered' | 'ended'

export type SiteVerdict =
  | { kind: 'admitted' }
  | { kind: 'refused'; reason: SiteRefusal }
  | { kind: 'unavailable' }

const REFUSALS: readonly SiteRefusal[] = ['signed_out', 'session_ended', 'not_member', 'not_registered', 'ended']

/**
 * A private-site answer: 401 `code: 'members_only'` (a members-only league)
 * or `'private_site'` (a Growth preview) → refused with its reason (an
 * unknown reason reads as signed_out); 503 `reason: 'check_unavailable'` →
 * unavailable. Anything else (a lockdown 503, a 404, …) is null.
 */
export function refusalFrom(status: number, body: unknown): SiteVerdict | null {
  const b = (body && typeof body === 'object' ? body : {}) as { code?: unknown; reason?: unknown }
  if (status === 401 && (b.code === 'members_only' || b.code === 'private_site')) {
    const reason = REFUSALS.includes(b.reason as SiteRefusal) ? (b.reason as SiteRefusal) : 'signed_out'
    return { kind: 'refused', reason }
  }
  if (status === 503 && b.reason === 'check_unavailable') return { kind: 'unavailable' }
  return null
}

const verdicts = new Map<string, SiteVerdict>()
const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of Array.from(listeners)) {
    try {
      fn()
    } catch {
      /* one listener's failure must not stop the others */
    }
  }
}

export function getVerdict(key: string): SiteVerdict | undefined {
  return verdicts.get(key)
}

export function setVerdict(key: string, v: SiteVerdict): void {
  if (verdicts.get(key) === v) return
  verdicts.set(key, v)
  notify()
}

export function clearVerdict(key: string): void {
  if (!verdicts.has(key)) return
  verdicts.delete(key)
  notify()
}

/** For useSyncExternalStore. */
export function subscribeVerdicts(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/**
 * The public credential changed in this tab (a sign-in or a sign-out, which
 * fire no storage event here): SiteAccessProvider calls this so the verdict
 * subscribers outside its subtree (the Fan AI bubble) re-read the credential.
 */
export function announceCredentialChange(): void {
  notify()
}

// The credential key whose `session_ended` raised the "your session has
// ended" notice, or null. Keeping the key lets SiteAccessProvider drop the
// notice as soon as the visitor moves on to any other credential (a fresh
// sign-in, then a deliberate sign-out never shows it).
let sessionEndedKey: string | null = null

/**
 * A public read was refused (ApiClient, on a private league only). Records
 * the verdict for the credential it was sent with. `session_ended` for a
 * member token ends the member session (only while that token is still the
 * stored one) and raises the "your session has ended" notice. A staff
 * credential is never cleared or ended here.
 */
export function reportPublicRefusal(sent: PublicCredential | null, verdict: SiteVerdict): void {
  if (verdict.kind === 'refused' && verdict.reason === 'session_ended' && sent?.kind === 'member') {
    sessionEndedKey = credentialKey(sent)
    setVerdict(credentialKey(sent), verdict)
    endMemberSessionFor(sent.token)
    return
  }
  setVerdict(credentialKey(sent), verdict)
}

/** Whether the wall should say the session ended (read, not consumed). */
export function hasSessionEndedNotice(): boolean {
  return sessionEndedKey !== null
}

/** The credential key the notice was raised for (null without a notice). */
export function sessionEndedNoticeKey(): string | null {
  return sessionEndedKey
}

export function clearSessionEndedNotice(): void {
  sessionEndedKey = null
}

let tenantRefreshRequested = false

/**
 * Ask LeagueContext to refetch /tenant, at most once per page load. Heals a
 * stale cached tenant: one that still says public while the gate refuses
 * (the site then learns it went private), or a lockdown 503 seen by the probe.
 */
export function requestTenantRefreshOnce(): void {
  if (tenantRefreshRequested || typeof window === 'undefined') return
  tenantRefreshRequested = true
  window.dispatchEvent(new Event('scorelm:tenant-refresh'))
}

// ── The probe ──────────────────────────────────────────────────────────────

const inFlight = new Map<string, Promise<void>>()

/**
 * Probe the gate once for `sent` (GET /public/site-access through `run`).
 * Idempotent per credential unless `force`, and a probe already in flight is
 * never doubled. Resolve → admitted. A reject that carries a `verdict` was
 * already reported by ApiClient; a 503 without one (a lockdown) asks for a
 * tenant refresh and reads as unavailable, as does a network error, a
 * timeout or a 404. No timers: nothing here retries by itself.
 */
export function ensureProbe(sent: PublicCredential | null, run: () => Promise<unknown>, force = false): void {
  const key = credentialKey(sent)
  if (inFlight.has(key)) return
  if (!force && verdicts.has(key)) return
  const p = Promise.resolve()
    .then(run)
    .then(
      () => {
        setVerdict(key, { kind: 'admitted' })
      },
      (err: unknown) => {
        const e = (err && typeof err === 'object' ? err : {}) as { verdict?: SiteVerdict | null; status?: unknown }
        if (e.verdict) {
          if (!verdicts.has(key)) setVerdict(key, e.verdict)
          return
        }
        if (e.status === 503) requestTenantRefreshOnce()
        setVerdict(key, { kind: 'unavailable' })
      },
    )
    .finally(() => {
      inFlight.delete(key)
    })
  inFlight.set(key, p)
}

export function __resetPublicAccessForTests(): void {
  privateSite = false
  privateKind = 'members'
  preferStaff = false
  verdicts.clear()
  inFlight.clear()
  sessionEndedKey = null
  tenantRefreshRequested = false
}
